// src/components/compliance/ReviewsTab.tsx
//
// Compliance Reviews inbox — periodic recertification / review records
// for compliance documents. Two lists:
//   - Scheduled  (not-yet-completed reviews, oldest scheduled first)
//   - Completed  (last decisions, newest first)
//
// Actions:
//   - Schedule a new review for a document (compliance.approve)
//   - Complete a scheduled review with an outcome + optional notes
//     (compliance.approve OR explicit reviewer assignment on the row)
//   - Delete a scheduled review (compliance.approve)
//
// Everything reads from the backend (`useGetComplianceReviewsQuery`)
// and mutates through the existing complianceApi.  No client-side
// authorization shortcuts — the load-bearing invariant matches the
// approvals tab:
//   compliance.approve OR reviewerUserId === current user.
// Neither TeamRole nor ProviderRole nor a Provider Assignment ever
// grants authority.

import { useMemo, useState } from 'react';
import {
  useGetComplianceReviewsQuery,
  useScheduleComplianceReviewMutation,
  useCompleteComplianceReviewMutation,
  useGetComplianceDocumentsQuery,
} from '../../api/complianceApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorState from '../common/ErrorState';
import Button from '../common/Button';
import { useAuth } from '../../hooks/useAuth';
import { useHasCapability } from '../../services/centralContext';
import {
  formatShortDate,
  messageFromComplianceError,
} from './complianceDisplay';
import type {
  ComplianceReview,
  ComplianceReviewOutcome,
} from '../../types/api';

interface ReviewsTabProps {
  workspaceId: string;
}

const OUTCOME_LABEL: Record<ComplianceReviewOutcome, string> = {
  passed: 'Passed',
  needs_attention: 'Needs attention',
  replaced: 'Replaced',
};

const OUTCOME_TONE: Record<ComplianceReviewOutcome, string> = {
  passed: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200',
  needs_attention: 'bg-amber-100 text-amber-800 ring-1 ring-amber-200',
  replaced: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200',
};

export default function ReviewsTab({ workspaceId: _workspaceId }: ReviewsTabProps) {
  const canApprove = useHasCapability('compliance', 'approve');
  const [showSchedule, setShowSchedule] = useState(false);
  const [completing, setCompleting] = useState<ComplianceReview | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  // Two queries so the two lists stay independent — schedule/complete
  // mutations invalidate `ComplianceReview:LIST` and both refetch.
  const {
    data: scheduled,
    isLoading: schedLoading,
    error: schedError,
    refetch: refetchScheduled,
  } = useGetComplianceReviewsQuery({ completed: false, limit: 200 });
  const {
    data: completed,
    isLoading: doneLoading,
    error: doneError,
  } = useGetComplianceReviewsQuery({ completed: true, limit: 200 });

  if (schedLoading || doneLoading) return <LoadingSpinner />;
  if (schedError) {
    return (
      <ErrorState
        title="Could not load reviews"
        message={messageFromComplianceError(schedError)}
        onRetry={refetchScheduled}
      />
    );
  }
  if (doneError) {
    return (
      <ErrorState
        title="Could not load completed reviews"
        message={messageFromComplianceError(doneError)}
      />
    );
  }

  const scheduledRows = scheduled?.rows ?? [];
  const completedRows = completed?.rows ?? [];

  return (
    <section className="space-y-6" data-testid="compliance-reviews-tab">
      {banner && (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {banner}
        </div>
      )}

      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Reviews</h2>
          <p className="text-sm text-gray-500 mt-1">
            Recertification and review records tied to compliance evidence.
          </p>
        </div>
        {canApprove && (
          <Button onClick={() => setShowSchedule(true)} data-testid="compliance-review-schedule-btn">
            Schedule review
          </Button>
        )}
      </header>

      <section
        className="rounded-lg border border-gray-200 bg-white"
        data-testid="compliance-reviews-scheduled"
      >
        <header className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Scheduled</h3>
          <span className="text-xs text-gray-500">
            {scheduledRows.length} review{scheduledRows.length === 1 ? '' : 's'}
          </span>
        </header>
        {scheduledRows.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500" data-testid="compliance-reviews-scheduled-empty">
            No scheduled reviews.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {scheduledRows.map((review) => (
              <ScheduledRow
                key={review.id}
                review={review}
                canApprove={canApprove}
                onComplete={() => setCompleting(review)}
              />
            ))}
          </ul>
        )}
      </section>

      <section
        className="rounded-lg border border-gray-200 bg-white"
        data-testid="compliance-reviews-completed"
      >
        <header className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Completed</h3>
          <span className="text-xs text-gray-500">
            {completedRows.length} review{completedRows.length === 1 ? '' : 's'}
          </span>
        </header>
        {completedRows.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500" data-testid="compliance-reviews-completed-empty">
            No completed reviews yet.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {completedRows.map((review) => (
              <li key={review.id} className="p-3" data-testid={`compliance-review-completed-${review.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm text-gray-900">
                      Document <span className="font-mono text-xs">{review.documentId}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Completed {formatShortDate(review.completedAt)}
                      {review.reviewerUserId ? ` · Reviewer ${review.reviewerUserId}` : ''}
                    </div>
                    {review.notes && (
                      <p className="text-xs text-gray-600 mt-1">{review.notes}</p>
                    )}
                  </div>
                  {review.outcome && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${OUTCOME_TONE[review.outcome]}`}
                      data-testid={`compliance-review-outcome-${review.outcome}`}
                    >
                      {OUTCOME_LABEL[review.outcome]}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {showSchedule && (
        <ScheduleReviewDialog
          onClose={() => setShowSchedule(false)}
          onError={setBanner}
        />
      )}
      {completing && (
        <CompleteReviewDialog
          review={completing}
          onClose={() => setCompleting(null)}
          onError={setBanner}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// ScheduledRow
// ---------------------------------------------------------------------------

function ScheduledRow({
  review,
  canApprove,
  onComplete,
}: {
  review: ComplianceReview;
  canApprove: boolean;
  onComplete: () => void;
}) {
  const { user: currentUser } = useAuth();
  const isAssignedReviewer =
    !!currentUser?.id &&
    !!review.reviewerUserId &&
    review.reviewerUserId === currentUser.id;
  const canAct = canApprove || isAssignedReviewer;

  const overdue = useMemo(() => {
    if (!review.scheduledFor) return false;
    return new Date(review.scheduledFor).getTime() < Date.now();
  }, [review.scheduledFor]);

  return (
    <li className="p-3" data-testid={`compliance-review-scheduled-${review.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm text-gray-900">
            Document <span className="font-mono text-xs">{review.documentId}</span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            Scheduled for {formatShortDate(review.scheduledFor)}
            {review.reviewerUserId ? ` · Reviewer ${review.reviewerUserId}` : ' · Unassigned reviewer'}
            {overdue && (
              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-800 ring-1 ring-red-200">
                Overdue
              </span>
            )}
          </div>
          {review.notes && (
            <p className="text-xs text-gray-600 mt-1">{review.notes}</p>
          )}
        </div>
        {canAct ? (
          <Button
            onClick={onComplete}
            data-testid={`compliance-review-complete-btn-${review.id}`}
          >
            Complete
          </Button>
        ) : (
          <span className="text-xs text-gray-500 self-center">
            You are not authorized to complete this review.
          </span>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------

function ScheduleReviewDialog({
  onClose,
  onError,
}: {
  onClose: () => void;
  onError: (msg: string) => void;
}) {
  const [documentId, setDocumentId] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [reviewerUserId, setReviewerUserId] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const { data: docs } = useGetComplianceDocumentsQuery({ limit: 200 });
  const [schedule, state] = useScheduleComplianceReviewMutation();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      await schedule({
        documentId: documentId.trim(),
        scheduledFor: new Date(scheduledFor).toISOString(),
        reviewerUserId: reviewerUserId.trim() || null,
        notes: notes.trim() || null,
      }).unwrap();
      onClose();
    } catch (e2) {
      const msg = messageFromComplianceError(e2);
      setErr(msg);
      onError(msg);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compliance-review-schedule-title"
    >
      <form
        onSubmit={handle}
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl space-y-3"
        data-testid="compliance-review-schedule-dialog"
      >
        <h3 id="compliance-review-schedule-title" className="text-lg font-semibold text-gray-900">
          Schedule review
        </h3>
        {err && (
          <div role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {err}
          </div>
        )}
        <div>
          <label htmlFor="rev-doc" className="block text-xs font-medium text-gray-700 mb-1">
            Document
          </label>
          <select
            id="rev-doc"
            value={documentId}
            onChange={(e) => setDocumentId(e.target.value)}
            className="w-full rounded border-gray-300 text-sm"
            required
            data-testid="compliance-review-doc-select"
          >
            <option value="" disabled>Choose…</option>
            {(docs?.rows ?? []).map((doc) => (
              <option key={doc.id} value={doc.id}>
                {doc.title} — {doc.requirement?.name ?? 'requirement'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="rev-when" className="block text-xs font-medium text-gray-700 mb-1">
            Scheduled for
          </label>
          <input
            id="rev-when"
            type="date"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
            required
            className="w-full rounded border-gray-300 text-sm"
            data-testid="compliance-review-schedule-date"
          />
        </div>
        <div>
          <label htmlFor="rev-reviewer" className="block text-xs font-medium text-gray-700 mb-1">
            Reviewer user id (optional)
          </label>
          <input
            id="rev-reviewer"
            value={reviewerUserId}
            onChange={(e) => setReviewerUserId(e.target.value)}
            className="w-full rounded border-gray-300 text-sm font-mono"
            data-testid="compliance-review-reviewer-input"
          />
        </div>
        <div>
          <label htmlFor="rev-notes" className="block text-xs font-medium text-gray-700 mb-1">
            Notes (optional)
          </label>
          <textarea
            id="rev-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded border-gray-300 text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            disabled={state.isLoading || !documentId.trim() || !scheduledFor}
            data-testid="compliance-review-schedule-confirm"
          >
            {state.isLoading ? 'Scheduling…' : 'Schedule'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function CompleteReviewDialog({
  review,
  onClose,
  onError,
}: {
  review: ComplianceReview;
  onClose: () => void;
  onError: (msg: string) => void;
}) {
  const [outcome, setOutcome] = useState<ComplianceReviewOutcome>('passed');
  const [notes, setNotes] = useState(review.notes ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [complete, state] = useCompleteComplianceReviewMutation();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    try {
      await complete({
        id: review.id,
        body: { outcome, notes: notes.trim() || null },
      }).unwrap();
      onClose();
    } catch (e2) {
      const msg = messageFromComplianceError(e2);
      setErr(msg);
      onError(msg);
    }
  };

  const outcomes: ComplianceReviewOutcome[] = ['passed', 'needs_attention', 'replaced'];

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compliance-review-complete-title"
    >
      <form
        onSubmit={handle}
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl space-y-3"
        data-testid="compliance-review-complete-dialog"
      >
        <h3 id="compliance-review-complete-title" className="text-lg font-semibold text-gray-900">
          Complete review
        </h3>
        <p className="text-xs text-gray-500">
          Document <span className="font-mono">{review.documentId}</span> · Scheduled{' '}
          {formatShortDate(review.scheduledFor)}
        </p>
        {err && (
          <div role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {err}
          </div>
        )}
        <fieldset className="space-y-1" data-testid="compliance-review-outcome-fieldset">
          <legend className="text-xs font-medium text-gray-700 mb-1">Outcome</legend>
          {outcomes.map((o) => (
            <label
              key={o}
              className={`flex items-center gap-2 rounded border p-2 cursor-pointer ${outcome === o ? 'border-indigo-500 bg-indigo-50/40' : 'border-gray-200'}`}
            >
              <input
                type="radio"
                name="review-outcome"
                value={o}
                checked={outcome === o}
                onChange={() => setOutcome(o)}
                data-testid={`compliance-review-outcome-radio-${o}`}
              />
              <span className="text-sm text-gray-900">{OUTCOME_LABEL[o]}</span>
            </label>
          ))}
        </fieldset>
        <div>
          <label htmlFor="rev-complete-notes" className="block text-xs font-medium text-gray-700 mb-1">
            Notes (optional)
          </label>
          <textarea
            id="rev-complete-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded border-gray-300 text-sm"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            disabled={state.isLoading}
            data-testid="compliance-review-complete-confirm"
          >
            {state.isLoading ? 'Saving…' : 'Complete review'}
          </Button>
        </div>
      </form>
    </div>
  );
}
